import { Compass, UserRound } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

function FooterPanelLink({ href, testId, onClick, accent = "cyan", children }) {
  const colors = accent === "violet"
    ? "hover:border-[#8A78CC]/50 hover:bg-[#8A78CC]/[0.07] hover:text-[#B9ACEE]"
    : "hover:border-[#4FAEBB]/50 hover:bg-[#4FAEBB]/[0.07] hover:text-[#75CFDA]";

  const className = `footer-glass-link group flex min-w-0 items-center justify-center rounded-xl border border-[#292931] px-2 py-3.5 text-center text-[9px] uppercase tracking-[0.11em] text-[#A0A0AB] transition-all duration-300 ${colors}`;
  const content = <span className="whitespace-nowrap">{children}</span>;

  return href.startsWith("/") ? (
    <Link to={href} onClick={onClick} className={className} data-testid={testId}>
      {content}
    </Link>
  ) : (
    <a href={href} onClick={onClick} className={className} data-testid={testId}>
      {content}
    </a>
  );
}

export default function Footer() {
  const { user } = useAuth();
  const openAccessMode = (mode) => () => {
    window.dispatchEvent(new CustomEvent("raava:access-mode", { detail: mode }));
  };

  return (
    <footer className="relative overflow-hidden px-6 py-16 sm:px-12 sm:py-20 lg:px-24" data-testid="main-footer">
      <div className="relative mx-auto max-w-6xl">
        <div className="grid gap-4 lg:grid-cols-2">
          <section
            className="footer-glass-panel footer-glass-panel-cyan raava-glow-panel rounded-[24px] border border-[#294048] p-5 sm:p-7"
            style={{ "--panel-accent-rgb": "79, 174, 187" }}
            aria-labelledby="footer-explore-title"
          >
            <div className="mb-6 flex items-start justify-between gap-5">
              <div className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#4FAEBB]/25 bg-[#4FAEBB]/10 text-[#64C2CF]"><Compass className="h-5 w-5" /></span>
                <div>
                  <h2 id="footer-explore-title" className="font-display text-lg text-[#F2F2F5]" style={{ fontWeight: 700 }}>Explore</h2>
                  <p className="mt-1 text-xs text-[#777783]">Projects, releases and development notes.</p>
                </div>
              </div>
              <span className="text-[9px] uppercase tracking-[0.22em] text-[#4FAEBB]/60">Discover</span>
            </div>
            <div className={`grid gap-2 ${user ? "sm:grid-cols-[0.9fr_0.85fr_1.25fr]" : "sm:grid-cols-2"}`}>
              <FooterPanelLink href="#manifest" testId="footer-manifest-link">My Work</FooterPanelLink>
              <FooterPanelLink href="#features" testId="footer-features-link">Projects</FooterPanelLink>
              {user && <FooterPanelLink href="/changelog" testId="footer-changelog-link">Changelog</FooterPanelLink>}
            </div>
          </section>

          <section
            className="footer-glass-panel footer-glass-panel-violet raava-glow-panel rounded-[24px] border border-[#39324E] p-5 sm:p-7"
            style={{ "--panel-accent-rgb": "138, 120, 204" }}
            aria-labelledby="footer-account-title"
          >
            <div className="mb-6 flex items-start justify-between gap-5">
              <div className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#8A78CC]/25 bg-[#8A78CC]/10 text-[#A493E0]"><UserRound className="h-5 w-5" /></span>
                <div>
                  <h2 id="footer-account-title" className="font-display text-lg text-[#F2F2F5]" style={{ fontWeight: 700 }}>Account</h2>
                  <p className="mt-1 text-xs text-[#777783]">Create, enter or manage your Raava identity.</p>
                </div>
              </div>
              <span className="text-[9px] uppercase tracking-[0.22em] text-[#8A78CC]/65">Member</span>
            </div>
            <div className="grid gap-2 sm:grid-cols-[0.9fr_0.85fr_1.25fr]">
              <FooterPanelLink href="#access" testId="footer-access-link" onClick={openAccessMode("login")} accent="violet">Sign In</FooterPanelLink>
              <FooterPanelLink href="#access" testId="footer-register-link" onClick={openAccessMode("register")} accent="violet">Register</FooterPanelLink>
              <FooterPanelLink href="/profile" testId="footer-dashboard-link" accent="violet">Dashboard</FooterPanelLink>
            </div>
          </section>
        </div>

        <div className="mt-8 border-t border-white/[0.06] pt-7">
          <div className="mb-7 flex flex-wrap gap-3" aria-label="Legal documents">
            <a href="/terms" className="footer-glass-legal rounded-xl border border-[#2A2A32] px-4 py-3 text-[9px] uppercase tracking-[0.2em] text-[#8E8E99] transition hover:border-[#35AFC0]/55 hover:text-white" data-testid="footer-terms-button">Terms</a>
            <a href="/software-license" className="footer-glass-legal rounded-xl border border-[#2A2A32] px-4 py-3 text-[9px] uppercase tracking-[0.2em] text-[#8E8E99] transition hover:border-[#7C5CFC]/55 hover:text-white" data-testid="footer-license-button">Software License</a>
            <a href="/privacy" className="footer-glass-legal rounded-xl border border-[#2A2A32] px-4 py-3 text-[9px] uppercase tracking-[0.2em] text-[#8E8E99] transition hover:border-[#FF3366]/45 hover:text-white" data-testid="footer-privacy-button">Privacy</a>
            <a href="mailto:support@batzenmodz.com" className="footer-glass-legal rounded-xl border border-[#2A2A32] px-4 py-3 text-[9px] uppercase tracking-[0.2em] text-[#8E8E99] transition hover:border-[#35AFC0]/55 hover:text-white" data-testid="footer-support-button">Email Support</a>
          </div>
          <div className="flex flex-col gap-3 text-[9px] uppercase tracking-[0.2em] text-[#62626D] sm:flex-row sm:items-center sm:justify-between">
            <span data-testid="footer-copyright">© 2026 Raava Framework. All rights reserved.</span>
            <span>Created by <span className="text-[#92929D]">BaTzEnMoDz</span></span>
          </div>
        </div>
      </div>
    </footer>
  );
}
